/**
 * Buildings that come out of a block.
 *
 * A креслярський камінь is put down like any other block, and the building it
 * carries stands up behind it - foundations, walls, windows, roof, the піч in
 * the corner and a смолоскип on the wall.
 *
 * **Why a building is written as pictures.** The хата below is seven rows of
 * seven characters, eight times over, and you can see the door and the
 * windows in the source. The obvious alternative - a list of (x, y, z, block)
 * triples - is unreadable at any size, and a wall with a hole in it looks
 * exactly like a wall.
 *
 * **Nothing is destroyed.** A building goes up only where there is room for
 * it: the whole volume has to be air, or grass and flowers that a block would
 * replace anyway. Somewhere with a tree or somebody's wall in it is refused,
 * with a reason, rather than being flattened. That rule is the difference
 * between a useful block and one nobody dares carry.
 *
 * Pure: a table of pictures and some arithmetic over a block-writing callback.
 * No world, no chunks, no engine.
 */

import {
  BlockId, doorId, stoveId, isReplaceable, isWater, type DoorFacing,
} from "../block/registry.js";
import { KREMLIN } from "./kremlin.js";

/** What a legend character puts down. A function where the facing matters. */
type Piece = number | ((facing: DoorFacing) => number);

/** What a building is allowed to push out of the way. */
export type Clearance =
  /** Air and grass only. Anything else refuses. */
  | "nothing"
  /**
   * Earth, rock, trees and water give way; anything anybody built does not.
   *
   * A хата can wait for a clearing. A fortress sixty-one blocks on a side
   * will never find one, so it levels its own site - but a building that
   * quietly ate the house you spent an evening on would be the last time you
   * ever dared put one down, so what a player made still stops it dead.
   */
  | "ground"
  /**
   * Everything goes. Nothing refuses it but the edge of the map.
   *
   * Asked for, after "ground" turned out to refuse more often than it
   * allowed: an orc lair anywhere in a hundred and forty thousand cells, or
   * one block of your own work, and the stone would not go down - with no way
   * to see which cell was the problem.
   *
   * The cost is real and is not hidden: a кремль placed carelessly will take
   * a хата, a chest and everything in it with no confirmation. What the game
   * can do is *say* so afterwards, which is what `builtInside` is for - a
   * count of what was somebody's work before the fortress landed on it.
   */
  | "everything";

interface Common {
  readonly name: string;
  readonly label: string;
  /**
   * Which cell of the ground floor sits directly behind the placed block.
   *
   * The doorway, so that walking at the stone walks you through the door.
   */
  readonly anchor: { x: number; z: number };
  readonly clears: Clearance;
  /**
   * Blocks this building will not touch, whatever its clearance says.
   *
   * One thing so far: an orc lair. A кремль that levelled the lairs it was
   * standing on would be a way to buy the end of the raids, and the raids are
   * the game. So the clearing steps over them and the building writes around
   * them - a lair inside the walls stays exactly where it was.
   */
  readonly spares?: readonly number[];
  /**
   * Fill any air or water under the footprint with this, down to solid
   * ground.
   *
   * Without it a building on a slope stands on stilts over the low side. The
   * хата is small enough to be placed on level ground by eye; nothing the
   * size of a fortress ever is.
   */
  readonly foundation?: number;
}

/**
 * A building drawn as pictures - see the note at the top of this file.
 */
export interface DrawnBuilding extends Common {
  /**
   * Layers from the ground up; within a layer, rows run from the front of the
   * building to its back, and characters across it left to right.
   */
  readonly layers: readonly (readonly string[])[];
  readonly legend: Readonly<Record<string, Piece>>;
}

/**
 * A building generated by code.
 *
 * **Why both kinds.** The хата is seven characters across and you can see its
 * door in the source; that is the right way to write a small building. A
 * fortress with seven towers, a curtain wall and four cathedrals is sixty-one
 * across and thirty-eight high - a hundred and forty thousand cells, which as
 * pictures would be some nine thousand lines of quoted strings that no one
 * could read, check or change. As code it is a loop for the wall and a
 * function for a tower, called seven times, and *that* is legible.
 *
 * The plan emits local coordinates. Rotation happens outside it, so a plan
 * never has to think about which way the building is turned - except for the
 * few pieces that genuinely care, like a door, which are handed the facing.
 */
export interface PlannedBuilding extends Common {
  /** The box it occupies, in local coordinates. */
  readonly size: { x: number; y: number; z: number };
  readonly plan: (
    put: (x: number, y: number, z: number, id: number) => void,
    facing: DoorFacing,
  ) => void;
}

export type Building = DrawnBuilding | PlannedBuilding;

function isDrawn(b: Building): b is DrawnBuilding {
  return (b as DrawnBuilding).layers !== undefined;
}

/** The box a building occupies, however it was written. */
export function extentOf(building: Building): { x: number; y: number; z: number } {
  if (!isDrawn(building)) return building.size;
  const layers = building.layers;
  return {
    x: layers[0]?.[0]?.length ?? 0,
    y: layers.length,
    z: layers[0]?.length ?? 0,
  };
}

/**
 * A хата: whitewashed daub, shuttered windows, a hipped thatch roof and a
 * chimney, from the photograph.
 *
 * Seven by seven and eight high including the roof. Small enough to raise in
 * a clearing, big enough to be a house rather than a hut.
 *
 * Legend:
 *   .  air        M  mazanka wall   W  window
 *   T  thatch     P  planks         B  brick
 *   D  door       S  піч            t  смолоскип
 */
export const HATA: Building = {
  name: "hata",
  label: "хата",
  anchor: { x: 3, z: 0 },
  // Small enough to find a clearing for, so it still refuses to flatten one.
  clears: "nothing",
  legend: {
    ".": BlockId.Air,
    M: BlockId.Mazanka,
    W: BlockId.OakWindow,
    T: BlockId.Thatch,
    P: BlockId.OakPlanks,
    B: BlockId.Brick,
    // The door faces the way the building does, so it opens onto the path.
    D: (facing) => doorId(0, facing, false, false),
    d: (facing) => doorId(0, facing, true, false),
    // The піч stands against the back wall with its mouth into the room.
    S: (facing) => stoveId(1, ((facing + 2) % 4) as DoorFacing),
    t: BlockId.Torch,
  },
  layers: [
    // 0 - the floor, a course of planks.
    [
      "PPPPPPP",
      "PPPPPPP",
      "PPPPPPP",
      "PPPPPPP",
      "PPPPPPP",
      "PPPPPPP",
      "PPPPPPP",
    ],
    /*
     * 1 - walls, the doorway open, the піч against the back wall.
     *
     * The stove stands *inside* the room rather than in the wall, and the
     * torch on the floor beside the door rather than in mid-air: a смолоскип
     * is a cross-rendered block that wants something under it, and the first
     * version had one hanging in the middle of the room.
     */
    [
      "MMMDMMM",
      "Mt....M",
      "M.....M",
      "M.....M",
      "M.....M",
      "M..S..M",
      "MMMMMMM",
    ],
    // 2 - the same, with the windows in, the upper half of the door, and the
    // chimney breast rising off the stove.
    [
      "MMWdWMM",
      "W.....W",
      "M.....M",
      "M.....M",
      "W.....W",
      "M..B..M",
      "MMMMMMM",
    ],
    // 3 - the top course, closed all the way round.
    [
      "MMMMMMM",
      "M.....M",
      "M.....M",
      "M.....M",
      "M.....M",
      "M..B..M",
      "MMMMMMM",
    ],
    // 4 - the eaves, oversailing the walls on every side, with the flue
    // through them.
    [
      "TTTTTTT",
      "TTTTTTT",
      "TTTTTTT",
      "TTTTTTT",
      "TTTTTTT",
      "TTTBTTT",
      "TTTTTTT",
    ],
    // 5-7 - the roof drawing in to a ridge, and the chimney standing clear
    // of it where the stove is.
    [
      ".......",
      ".TTTTT.",
      ".TTTTT.",
      ".TTTTT.",
      ".TTTTT.",
      ".TTBTT.",
      ".......",
    ],
    [
      ".......",
      ".......",
      "..TTT..",
      "..TTT..",
      "..TTT..",
      "...B...",
      ".......",
    ],
    [
      ".......",
      ".......",
      ".......",
      "...T...",
      ".......",
      ".......",
      ".......",
    ],
  ],
};

export const BUILDINGS: readonly Building[] = [HATA, KREMLIN];

/**
 * The building a креслярський камінь carries.
 *
 * The stone names its building rather than pointing at it, so the block
 * registry does not have to know what a кремль is - see PLAN_STONES.
 */
export function buildingNamed(name: string | undefined): Building | undefined {
  return BUILDINGS.find((b) => b.name === name);
}

/**
 * How much of somebody's work is standing where this building would go.
 *
 * Zero nearly always. It is counted so the game can say "that took eleven
 * blocks you had placed" after the fact - a building that clears everything
 * cannot ask first without becoming the thing that was too fussy to use, but
 * it can at least not pretend nothing happened.
 *
 * Natural ground is not counted: earth, rock and trees are what the site was
 * made of, and reporting them would bury the number that matters.
 */
export function builtInside(
  building: Building, facing: DoorFacing,
  bx: number, by: number, bz: number,
  read: (x: number, y: number, z: number) => number,
  isNatural: (id: number) => boolean,
): number {
  let built = 0;
  for (const cell of volumeOf(building, facing, bx, by, bz)) {
    const there = read(cell.x, cell.y, cell.z);
    if (there === BlockId.Air) continue;
    if (isNatural(there)) continue;
    built++;
  }
  return built;
}

/** Why a building could not be raised. */
export type RaiseRefusal = "no-room" | "off-the-map" | "no-such-building";
export type RaiseResult = "ok" | RaiseRefusal;

/**
 * Turn a position inside the building into an offset from the block.
 *
 * The building is written facing -Z, and `facing` is which way the placed
 * block looks - which is at the player. The building goes the other way, so
 * local +z runs away from whoever put the stone down.
 */
export function offsetFor(
  building: Building, facing: DoorFacing, lx: number, lz: number,
): { dx: number; dz: number } {
  const x = lx - building.anchor.x;
  // The extra step is what makes it stand *behind* the block rather than on
  // top of it.
  const z = lz - building.anchor.z + 1;
  switch (facing) {
    case 0: return { dx: x, dz: z };
    case 1: return { dx: -z, dz: x };
    case 2: return { dx: -x, dz: -z };
    default: return { dx: z, dz: -x };
  }
}

/**
 * Every cell a building would occupy, and what would go in it.
 *
 * Air is included: the room inside a хата is part of the building. It writes
 * nothing, but it is space the building needs, and `raise` has to know about
 * it - see there.
 */
export function* cellsOf(
  building: Building, facing: DoorFacing,
  bx: number, by: number, bz: number,
): Generator<{ x: number; y: number; z: number; id: number }> {
  if (!isDrawn(building)) {
    // A plan writes wherever it likes inside its box; collected rather than
    // streamed because a generator cannot be driven by a callback.
    /*
     * Resolved against itself before anything leaves here: last write wins,
     * air included.
     *
     * A plan is written in layers the way a building is actually built - lay
     * the paving, run the wall along it, stand the tower on the wall, then
     * cut the gateway back out through all three. That last step only works
     * if writing air over brick erases the brick, and `raise` deliberately
     * skips air cells when it writes (so a хата's rooms do not gouge holes in
     * the ground). Without this map the кремль came out with its gateway
     * bricked up - the wall won, because it was written first.
     */
    const size = building.size;
    const resolved = new Map<number, { x: number; y: number; z: number; id: number }>();
    building.plan((lx, ly, lz, id) => {
      /*
       * A plan may only write inside its own box.
       *
       * `raise` checks the box for clearance and then writes what the plan
       * emits; a cell outside it would be written without ever having been
       * checked, which is precisely the "quietly ate somebody's хата" bug the
       * clearance pass exists to prevent. Dropped here so production is safe,
       * and counted by `strayCells` so a test is loud about it.
       */
      if (lx < 0 || ly < 0 || lz < 0) return;
      if (lx >= size.x || ly >= size.y || lz >= size.z) return;
      const { dx, dz } = offsetFor(building, facing, lx, lz);
      resolved.set((ly * size.z + lz) * size.x + lx,
        { x: bx + dx, y: by + ly, z: bz + dz, id });
    }, facing);
    yield* resolved.values();
    return;
  }
  for (let ly = 0; ly < building.layers.length; ly++) {
    const layer = building.layers[ly]!;
    for (let lz = 0; lz < layer.length; lz++) {
      const row = layer[lz]!;
      for (let lx = 0; lx < row.length; lx++) {
        const piece = building.legend[row[lx]!];
        const id = piece === undefined ? BlockId.Air
          : typeof piece === "number" ? piece : piece(facing);
        const { dx, dz } = offsetFor(building, facing, lx, lz);
        yield { x: bx + dx, y: by + ly, z: bz + dz, id };
      }
    }
  }
}

/**
 * How many blocks a plan tried to put outside its own box.
 *
 * Always zero, and a test says so. It is here because the failure it catches
 * is invisible in the game: a tower whose roof overhangs the declared size
 * simply appears, having skipped every check that makes a building safe to
 * put down.
 */
export function strayCells(building: Building, facing: DoorFacing): number {
  if (isDrawn(building)) return 0;
  const size = building.size;
  let stray = 0;
  building.plan((lx, ly, lz) => {
    if (lx < 0 || ly < 0 || lz < 0 ||
        lx >= size.x || ly >= size.y || lz >= size.z) stray++;
  }, facing);
  return stray;
}

/**
 * Every cell of the box the building stands in, block or not.
 *
 * Separate from `cellsOf` because what a building *writes* and what it
 * *occupies* are different questions, and the second one is the one that
 * decides whether it may go up at all. The room inside a хата holds no block
 * and is still part of the хата: checking only the walls once let a building
 * close around a standing oak, which then had a tree through the middle of it.
 */
export function* volumeOf(
  building: Building, facing: DoorFacing,
  bx: number, by: number, bz: number,
): Generator<{ x: number; y: number; z: number }> {
  const size = extentOf(building);
  for (let ly = 0; ly < size.y; ly++) {
    for (let lz = 0; lz < size.z; lz++) {
      for (let lx = 0; lx < size.x; lx++) {
        const { dx, dz } = offsetFor(building, facing, lx, lz);
        yield { x: bx + dx, y: by + ly, z: bz + dz };
      }
    }
  }
}

/**
 * Every cell of the ground floor, which is what the building has to stand on.
 */
export function* footprintOf(
  building: Building, facing: DoorFacing, bx: number, bz: number,
): Generator<{ x: number; z: number }> {
  const size = extentOf(building);
  for (let lz = 0; lz < size.z; lz++) {
    for (let lx = 0; lx < size.x; lx++) {
      const { dx, dz } = offsetFor(building, facing, lx, lz);
      yield { x: bx + dx, z: bz + dz };
    }
  }
}

/**
 * Raise a building behind a block.
 *
 * Three passes, and each one earns its place.
 *
 * **Bounds first, for the whole volume.** Running off the edge of the map is a
 * different answer from "somebody's wall is in the way", and the player should
 * be told which.
 *
 * **Then the whole volume, not just the cells that get a block.** The room
 * inside a хата is part of it: checking only the walls let a building go up
 * around a standing oak, which then had a tree through the middle of it and
 * a roof resting on its branches.
 *
 * **Then write.** Nothing at all is written until everything has passed, so a
 * refusal leaves the world exactly as it was. A building that got halfway up
 * and stopped would be worse than one that never started.
 */
/**
 * How deep a building will underpin itself before giving up.
 *
 * Twelve: enough to bridge a gully or reach down through shallow water to the
 * bed, and shallow enough that a fortress placed on a cliff edge refuses
 * rather than growing a hundred-block stone leg down to the sea floor.
 */
export const FOUNDATION_DEPTH = 12;

/**
 * What ground gives way to a building that levels its site.
 *
 * Listed rather than derived, and it fails the safe way round: anything not
 * named here stops the build. Get this list wrong by omission and a кремль
 * refuses on a patch of gravel, which is a nuisance; get it wrong by
 * defaulting to "natural unless known otherwise" and one refusal becomes
 * somebody's хата eaten without warning. Nuisance is the right failure.
 */
export function isGround(id: number, isNatural: (id: number) => boolean): boolean {
  return isReplaceable(id) || isNatural(id);
}

/**
 * Raise a building behind a block.
 *
 * Four passes, and each one earns its place.
 *
 * **Bounds first, over the whole box.** Running off the edge of the map is a
 * different answer from "somebody's wall is in the way", and the player should
 * be told which.
 *
 * **Then clearance, over the whole box** - not just the cells that get a
 * block. See `volumeOf` for what that is worth.
 *
 * **Then the footprint**, if the building underpins itself: air and water
 * below it become foundation. This only ever fills emptiness, so it cannot
 * destroy anything, but it can still run off the bottom of the map.
 *
 * **Then write.** Nothing at all is written until everything has passed, so a
 * refusal leaves the world exactly as it was. A building that got halfway up
 * and stopped would be worse than one that never started.
 */
export function raise(
  building: Building, facing: DoorFacing,
  bx: number, by: number, bz: number,
  read: (x: number, y: number, z: number) => number,
  inside: (x: number, y: number, z: number) => boolean,
  write: (x: number, y: number, z: number, id: number) => void,
  /** Which blocks count as untouched ground. Only asked when it levels. */
  isNatural: (id: number) => boolean = () => false,
  /**
   * How far down the foundation may reach.
   *
   * The waterline, in practice. Without it a fortress whose edge hangs over
   * the sea underpins itself down through the water to the seabed, and every
   * cell of that is a below-sea-level structure the water model cannot cope
   * with once somebody digs into it: four hundred random digs left three
   * hundred and thirty-seven cells of water hanging in the air.
   *
   * Stopping at the waterline leaves a stone plinth standing out of the
   * water, which is how the thing in the photograph sits above its river.
   */
  floorY = -Infinity,
): RaiseResult {
  for (const cell of volumeOf(building, facing, bx, by, bz)) {
    if (!inside(cell.x, cell.y, cell.z)) return "off-the-map";
  }
  if (building.clears !== "everything") {
    for (const cell of volumeOf(building, facing, bx, by, bz)) {
      const there = read(cell.x, cell.y, cell.z);
      const ok = building.clears === "ground"
        ? isGround(there, isNatural)
        : isReplaceable(there);
      if (!ok) return "no-room";
    }
  }

  /*
   * The foundation, worked out before anything is written.
   *
   * Collected rather than filled as we go, because a footprint cell that runs
   * out of world halfway down has to refuse the *whole* building, and by then
   * the columns beside it would already be standing in the ground.
   */
  const footing: { x: number; y: number; z: number; id: number }[] = [];
  if (building.foundation !== undefined) {
    for (const { x, z } of footprintOf(building, facing, bx, bz)) {
      for (let d = 1; d <= FOUNDATION_DEPTH; d++) {
        const y = by - d;
        if (y < floorY) break;
        if (!inside(x, y, z)) return "off-the-map";
        const there = read(x, y, z);
        // Stop at the first thing solid enough to build on. Water and air are
        // not, and everything else is.
        if (!isReplaceable(there) && !isWater(there)) break;
        footing.push({ x, y, z, id: building.foundation });
      }
    }
  }

  for (const cell of footing) write(cell.x, cell.y, cell.z, cell.id);
  /*
   * The box is emptied before the building goes into it.
   *
   * A building that only writes its own blocks leaves whatever was in the
   * gaps: place a кремль over a wood and the trees stay, growing up through
   * the courtyard and out of the cathedral roofs.
   */
  const spared = new Set(building.spares ?? []);
  if (building.clears !== "nothing") {
    for (const cell of volumeOf(building, facing, bx, by, bz)) {
      const there = read(cell.x, cell.y, cell.z);
      // Only what is actually there. A кремль's box is a hundred and forty
      // thousand cells and most of them are already sky; writing air over air
      // would hand the world twice as many edits as the building has blocks.
      if (there === BlockId.Air) continue;
      if (spared.has(there)) continue;
      write(cell.x, cell.y, cell.z, BlockId.Air);
    }
  }
  for (const cell of cellsOf(building, facing, bx, by, bz)) {
    if (cell.id === BlockId.Air) continue;
    // Nor is a spared block built over: sparing it from the clearing and then
    // laying a wall on top of it would spare nothing.
    if (spared.has(read(cell.x, cell.y, cell.z))) continue;
    write(cell.x, cell.y, cell.z, cell.id);
  }
  return "ok";
}
